import { useAuth } from '../contexts/AuthContext'
import { API_BASE_URL } from '../config'

export type FeedbackType = 'bug' | 'feature' | 'feedback'
export type FeedbackStatus = 'new' | 'in_progress' | 'planned' | 'resolved' | 'wont_fix'

export interface FeedbackAttachment {
  s3Key: string
  fileName: string
  contentType: string
  uploadedAt: string
}

export interface Feedback {
  id: string
  tenantId: string
  tenantEmail: string
  type: FeedbackType
  title: string
  description: string
  pageUrl: string
  userAgent: string
  attachments: FeedbackAttachment[]
  status: FeedbackStatus
  adminNotes: string
  createdAt: string
  updatedAt: string
}

// Uploads go straight to S3 via a presigned URL rather than through the
// Lambda — same rationale as documentService.ts's uploadFileToS3.
const uploadFileToS3 = async (uploadUrl: string, file: File | Blob, contentType: string): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status}`)
  }
}

// Authenticated: tenant-side bug report / feature request / general
// feedback submission, with optional attachments (including a
// client-captured screenshot — see FeedbackWidget.tsx). Mirrors
// documentService.ts's 2-step upload conventions.
export const useFeedbackService = () => {
  const { token, authFetch } = useAuth()

  const submitFeedback = async (input: { type: FeedbackType; title: string; description: string }): Promise<Feedback> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...input,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      }),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to submit feedback: ${response.status}`)
    }

    return data.feedback
  }

  // fileName/contentType are taken as explicit params (rather than always
  // reading them off a File) so a captured screenshot Blob — which has
  // neither — can go through the exact same upload path as a regular
  // attached file.
  const uploadAttachment = async (feedbackId: string, file: File | Blob, fileName: string, contentType: string): Promise<FeedbackAttachment> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await authFetch(`${API_BASE_URL}/feedback/${feedbackId}/attachments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName, contentType }),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to start attachment upload: ${response.status}`)
    }

    await uploadFileToS3(data.uploadUrl, file, contentType)

    return data.attachment
  }

  return {
    submitFeedback,
    uploadAttachment,
  }
}
